const TRAILING_NUMBER = /(\d+)[^0-9a-z]*$/i;

export type SuggestableExternalAccount = {
  id: string;
  bankName?: string | null;
  nickname?: string | null;
  last4?: string | null;
};

export type SuggestableLedgerAccount = {
  id: string;
  name?: string | null;
  fullName?: string | null;
  bankName?: string | null;
  bankAccountLast4?: string | null;
};

type Matcher = (
  external: SuggestableExternalAccount,
  ledger: SuggestableLedgerAccount,
) => boolean;

// Strongest first: the first tier with any candidate decides, and a tie there yields nothing.
const TIERS: Matcher[] = [
  lastFourMatches,
  (external, ledger) => ledgerNamesMatch(external.nickname, ledger),
  (external, ledger) => ledgerNamesMatch(external.bankName, ledger),
];

/**
 * Suggests a ledger account for each unmapped external account, keyed by external account id.
 * A ledger account that is already mapped, or that two external accounts would both claim, is
 * never suggested: an ambiguous guess costs the reader more than an empty select does.
 */
export function suggestLedgerAccountMappings(
  externalAccounts: SuggestableExternalAccount[],
  ledgerAccounts: SuggestableLedgerAccount[],
  currentMappings: Record<string, string> = {},
): Record<string, string> {
  const mappedLedgerIds = new Set(
    Object.values(currentMappings).filter(Boolean),
  );
  const candidates = ledgerAccounts.filter(
    (ledger) => !mappedLedgerIds.has(ledger.id),
  );
  const suggestions: Record<string, string> = {};

  externalAccounts
    .filter((external) => !currentMappings[external.id])
    .forEach((external) => {
      const match = uniqueMatch(external, candidates);
      if (match) suggestions[external.id] = match;
    });

  return withoutSharedLedgerAccounts(suggestions);
}

function uniqueMatch(
  external: SuggestableExternalAccount,
  candidates: SuggestableLedgerAccount[],
): string | undefined {
  for (const matches of TIERS) {
    const found = candidates.filter((ledger) => matches(external, ledger));
    if (found.length === 1) return found[0].id;
    if (found.length > 1) return undefined;
  }
  return undefined;
}

function withoutSharedLedgerAccounts(
  suggestions: Record<string, string>,
): Record<string, string> {
  const claims: Record<string, number> = {};
  Object.values(suggestions).forEach((ledgerId) => {
    claims[ledgerId] = (claims[ledgerId] || 0) + 1;
  });
  const unique: Record<string, string> = {};
  Object.entries(suggestions).forEach(([externalId, ledgerId]) => {
    if (claims[ledgerId] === 1) unique[externalId] = ledgerId;
  });
  return unique;
}

function lastFourDigits(value?: string | null): string {
  const digits = (value || "").replace(/\D/g, "");
  return digits.length >= 4 ? digits.slice(-4) : "";
}

function trailingNumberLastFour(name?: string | null): string {
  const match = TRAILING_NUMBER.exec(name || "");
  if (!match) return "";
  // "1234" in "Checking 1234" identifies the account; in "Acct1234" it is part of a word.
  const preceding = (name || "")[match.index - 1];
  if (preceding && /[0-9a-z]/i.test(preceding)) return "";
  return lastFourDigits(match[1]);
}

function lastFourMatches(
  external: SuggestableExternalAccount,
  ledger: SuggestableLedgerAccount,
): boolean {
  const last4 = lastFourDigits(external.last4);
  if (!last4) return false;
  return (
    last4 === lastFourDigits(ledger.bankAccountLast4) ||
    last4 === trailingNumberLastFour(ledger.name)
  );
}

function nameTokens(value?: string | null): string[] {
  return (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);
}

function containsTokens(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) return false;
  return haystack.some((_, start) =>
    needle.every((token, offset) => haystack[start + offset] === token),
  );
}

function ledgerNamesMatch(
  bankName: string | null | undefined,
  ledger: SuggestableLedgerAccount,
): boolean {
  return [ledger.bankName, ledger.fullName || ledger.name].some((name) =>
    namesMatch(bankName, name),
  );
}

function namesMatch(bankName?: string | null, ledgerName?: string | null) {
  const bank = nameTokens(bankName);
  const ledger = nameTokens(ledgerName);
  if (bank.length === 0 || ledger.length === 0) return false;
  return containsTokens(ledger, bank) || containsTokens(bank, ledger);
}
